<template>
  <!-- 分享卡片弹层：画布渲染成绩单，可保存 / 系统分享。统计页与计时完成页共用 -->
  <div
    v-if="showShare"
    class="absolute inset-0 z-50 flex flex-col items-center justify-center p-5 animate-fade-in"
    style="background: rgba(0, 0, 0, 0.55); backdrop-filter: blur(4px)"
    @click.self="closeShare"
  >
    <div class="w-full max-w-xs flex flex-col items-center">
      <!-- 卡片轮播：左右滑动切换不同维度的成绩单 -->
      <div
        class="w-full overflow-hidden rounded-2xl select-none"
        style="touch-action: pan-y"
        @touchstart="onTouchStart"
        @touchmove="onTouchMove"
        @touchend="onTouchEnd"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp"
        @pointerleave="onPointerUp"
      >
        <div
          class="flex transition-transform duration-300 ease-out"
          :style="{ transform: `translateX(-${activeCard * 100}%)` }"
        >
          <div
            v-for="(card, i) in shareCards"
            :key="card.key"
            class="w-full shrink-0 px-0.5"
          >
            <img
              :src="card.img"
              :alt="card.key"
              class="w-full rounded-2xl shadow-2xl select-none"
              style="max-height: 60vh; object-fit: contain"
              draggable="false"
            />
            <span class="sr-only">{{ i }}</span>
          </div>
        </div>
      </div>
      <!-- 圆点指示器 -->
      <div class="flex items-center gap-2 mt-4">
        <button
          v-for="(card, i) in shareCards"
          :key="card.key"
          @click="activeCard = i"
          class="h-2 rounded-full transition-all"
          :class="activeCard === i ? 'w-6 bg-amber-500' : 'w-2 bg-white/30'"
          :aria-label="card.label"
        ></button>
      </div>
      <div class="text-xs text-white/70 mt-2 font-medium">
        {{ shareCards[activeCard] && shareCards[activeCard].label }}
      </div>
      <div class="grid grid-cols-2 gap-3 w-full mt-4">
        <button
          class="py-3 rounded-xl font-bold text-sm bg-white/15 text-white active:scale-95 transition"
          @click="closeShare"
        >
          {{ t.share_close || "关闭" }}
        </button>
        <button
          class="py-3 rounded-xl font-bold text-sm bg-amber-500 text-white active:scale-95 transition flex items-center justify-center gap-2"
          @click="saveOrShare"
        >
          <icon-base
            :name="canNativeShare ? 'Share' : 'Download'"
            class="w-4 h-4"
          ></icon-base>
          {{
            canNativeShare
              ? t.share_do_share || "分享"
              : t.share_save_img || "保存图片"
          }}
        </button>
      </div>
      <div
        v-if="toast"
        class="mt-3 text-xs text-white/90 bg-black/40 px-3 py-1.5 rounded-full animate-fade-in"
      >
        {{ toast }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ShareCard",
  props: ["t", "history", "summary", "monthHeatmap", "isSameDay"],
  data() {
    return {
      showShare: false,
      shareCards: [], // [{ key, label, img, blob }]
      activeCard: 0,
      toast: "",
    };
  },
  computed: {
    // 今日的专注记录（按时间倒序），用于「今日」卡片明细
    todayRecords() {
      const today = new Date();
      return this.history
        .filter((h) => this.isSameDay(new Date(h.date), today))
        .sort((a, b) => new Date(b.date) - new Date(a.date));
    },
    // 今日心流总时长（分钟），用于分享卡片
    todayMinutes() {
      return this.todayRecords.reduce((acc, h) => acc + h.duration, 0);
    },
    // 本周（周一至周日）每日心流时长 + 汇总，用于「本周」卡片柱状图
    weekStats() {
      const now = new Date();
      const monday = new Date(now);
      // getDay(): 周日=0，转换为以周一为一周起点
      const offset = (now.getDay() + 6) % 7;
      monday.setDate(now.getDate() - offset);
      monday.setHours(0, 0, 0, 0);
      const days = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(monday);
        d.setDate(monday.getDate() + i);
        const mins = this.history
          .filter((h) => this.isSameDay(new Date(h.date), d))
          .reduce((acc, h) => acc + h.duration, 0);
        days.push({ date: d, minutes: mins });
      }
      const totalMins = days.reduce((acc, d) => acc + d.minutes, 0);
      const activeDays = days.filter((d) => d.minutes > 0).length;
      return { days, totalMins, activeDays };
    },
    // 是否支持系统级文件分享（移动端 / 部分桌面壳）
    canNativeShare() {
      return (
        typeof navigator !== "undefined" &&
        !!navigator.canShare &&
        !!navigator.share
      );
    },
  },
  methods: {
    // 公开入口：渲染所有维度的卡片并弹出，供统计页/完成页调用
    open() {
      const t = this.t;
      const types = [
        { key: "today", label: t.share_card_today_title || "今日心流" },
        { key: "week", label: t.share_card_week_title || "本周累计" },
        { key: "total", label: t.share_card_total_title || "累计总览" },
      ];
      this.shareCards = types.map((tp) => {
        const canvas = this.drawCard(tp.key);
        return {
          key: tp.key,
          label: tp.label,
          img: canvas.toDataURL("image/png"),
          blob: null,
          canvas,
        };
      });
      // 异步补齐 blob（分享/保存用），不阻塞预览
      this.shareCards.forEach((c) =>
        c.canvas.toBlob((b) => (c.blob = b), "image/png")
      );
      this.activeCard = 0;
      this.toast = "";
      this.showShare = true;
    },
    closeShare() {
      this.showShare = false;
    },
    // 分钟数格式化为「Xh Ym / Xh / Ym」
    fmtMins(m) {
      if (!m) return "0m";
      const h = Math.floor(m / 60);
      const mm = m % 60;
      if (h > 0) return mm > 0 ? `${h}h ${mm}m` : `${h}h`;
      return `${mm}m`;
    },
    // —— 轮播滑动手势 ——
    onTouchStart(e) {
      this._touchX = e.touches[0].clientX;
      this._touchDx = 0;
    },
    onTouchMove(e) {
      this._touchDx = e.touches[0].clientX - this._touchX;
    },
    onTouchEnd() {
      if (this._touchDx < -40 && this.activeCard < this.shareCards.length - 1)
        this.activeCard++;
      else if (this._touchDx > 40 && this.activeCard > 0) this.activeCard--;
      this._touchDx = 0;
    },
    // —— 指针拖动（桌面鼠标 / 触控笔兜底）——
    // 触摸已由 touch 事件处理，这里跳过 touch 避免重复切卡。
    onPointerDown(e) {
      if (e.pointerType === "touch") return;
      this._dragging = true;
      this._pointerX = e.clientX;
      this._pointerDx = 0;
    },
    onPointerMove(e) {
      if (!this._dragging) return;
      this._pointerDx = e.clientX - this._pointerX;
    },
    onPointerUp() {
      if (!this._dragging) return;
      this._dragging = false;
      if (this._pointerDx < -40 && this.activeCard < this.shareCards.length - 1)
        this.activeCard++;
      else if (this._pointerDx > 40 && this.activeCard > 0) this.activeCard--;
      this._pointerDx = 0;
    },
    // 圆角矩形（兼容无原生 roundRect 的环境）
    roundRect(ctx, x, y, w, h, r) {
      if (ctx.roundRect) {
        ctx.beginPath();
        ctx.roundRect(x, y, w, h, r);
        return;
      }
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + w, y, x + w, y + h, r);
      ctx.arcTo(x + w, y + h, x, y + h, r);
      ctx.arcTo(x, y + h, x, y, r);
      ctx.arcTo(x, y, x + w, y, r);
      ctx.closePath();
    },
    // 卡片通用底图：背景 + 光晕 + 品牌 + 标题 + 页脚，返回 { ctx, W, H, PAD, colors }
    drawChrome(title) {
      const W = 1080;
      const H = 1500;
      const PAD = 90;
      const t = this.t;
      const canvas = document.createElement("canvas");
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext("2d");
      const colors = {
        amber: "#f59e0b",
        amberLt: "#fbbf24",
        muted: "rgba(255,255,255,0.55)",
      };
      // 背景渐变
      const bg = ctx.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0, "#1b2030");
      bg.addColorStop(1, "#0e111b");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);
      // 顶部琥珀光晕
      const glow = ctx.createRadialGradient(W / 2, 120, 40, W / 2, 120, 620);
      glow.addColorStop(0, "rgba(245,158,11,0.18)");
      glow.addColorStop(1, "rgba(245,158,11,0)");
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, W, 500);
      // 品牌
      ctx.textAlign = "center";
      ctx.fillStyle = colors.amber;
      ctx.font = "800 56px system-ui, -apple-system, sans-serif";
      ctx.fillText("Z E N O", W / 2, 165);
      ctx.fillStyle = colors.muted;
      ctx.font = "400 30px system-ui, -apple-system, sans-serif";
      ctx.fillText(t.share_card_slogan || "专注当下，心流不止", W / 2, 220);
      // 标题
      ctx.fillStyle = "#fff";
      ctx.font = "700 46px system-ui, -apple-system, sans-serif";
      ctx.fillText(title, W / 2, 350);
      // 页脚
      ctx.fillStyle = "rgba(255,255,255,0.4)";
      ctx.font = "500 26px system-ui, -apple-system, sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(new Date().toLocaleDateString(), PAD, H - 70);
      ctx.textAlign = "right";
      ctx.fillText("Generated by Zeno", W - PAD, H - 70);
      return { canvas, ctx, W, H, PAD, colors };
    },
    // 居中大号主数据 + 标签
    drawHero(ctx, W, colors, value, label) {
      ctx.textAlign = "center";
      ctx.fillStyle = colors.amberLt;
      ctx.font = "800 130px system-ui, -apple-system, sans-serif";
      ctx.fillText(value, W / 2, 540);
      ctx.fillStyle = "rgba(255,255,255,0.8)";
      ctx.font = "600 34px system-ui, -apple-system, sans-serif";
      ctx.fillText(label, W / 2, 605);
    },
    // 按卡片类型绘制：today / week / total
    drawCard(type) {
      if (type === "week") return this.drawWeekCard();
      if (type === "total") return this.drawTotalCard();
      return this.drawTodayCard();
    },
    // 卡片一：今日心流（主数据 + 今日明细）
    drawTodayCard() {
      const t = this.t;
      const { canvas, ctx, W, PAD, colors } = this.drawChrome(
        t.share_card_today_title || "今日心流"
      );
      this.drawHero(
        ctx,
        W,
        colors,
        this.fmtMins(this.todayMinutes),
        t.share_today_flow || "今日心流"
      );
      // 两项小数据：完成次数 + 主要标签
      const recs = this.todayRecords;
      const tagMins = {};
      recs.forEach((r) => (tagMins[r.tag] = (tagMins[r.tag] || 0) + r.duration));
      let topTag = "—";
      let max = -1;
      Object.keys(tagMins).forEach((k) => {
        if (tagMins[k] > max) {
          max = tagMins[k];
          topTag = k;
        }
      });
      const cols = [W * 0.3, W * 0.7];
      const small = [
        { num: String(recs.length), label: t.share_today_sessions || "今日完成" },
        { num: topTag, label: t.share_main_tag || "主要专注" },
      ];
      small.forEach((st, i) => {
        ctx.textAlign = "center";
        ctx.fillStyle = colors.amberLt;
        ctx.font = "700 56px system-ui, -apple-system, sans-serif";
        ctx.fillText(st.num, cols[i], 720);
        ctx.fillStyle = colors.muted;
        ctx.font = "500 28px system-ui, -apple-system, sans-serif";
        ctx.fillText(st.label, cols[i], 765);
      });
      // 今日明细容器
      const boxX = PAD;
      const boxY = 820;
      const boxW = W - PAD * 2;
      const boxH = 480;
      ctx.fillStyle = "rgba(255,255,255,0.05)";
      this.roundRect(ctx, boxX, boxY, boxW, boxH, 36);
      ctx.fill();
      if (recs.length === 0) {
        ctx.textAlign = "center";
        ctx.fillStyle = colors.muted;
        ctx.font = "500 30px system-ui, -apple-system, sans-serif";
        ctx.fillText(t.no_record || "暂无专注记录", W / 2, boxY + boxH / 2 + 10);
      } else {
        const rowH = 78;
        const maxRows = Math.floor((boxH - 60) / rowH);
        recs.slice(0, maxRows).forEach((r, i) => {
          const y = boxY + 40 + i * rowH;
          ctx.textAlign = "left";
          ctx.fillStyle = "#fff";
          ctx.font = "600 34px system-ui, -apple-system, sans-serif";
          ctx.fillText(r.tag, boxX + 48, y + 30);
          ctx.fillStyle = colors.muted;
          ctx.font = "400 26px system-ui, -apple-system, sans-serif";
          ctx.fillText(
            new Date(r.date).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
            boxX + 48,
            y + 64
          );
          ctx.textAlign = "right";
          ctx.fillStyle = colors.amberLt;
          ctx.font = "700 36px system-ui, -apple-system, sans-serif";
          ctx.fillText("+" + r.duration + "m", boxX + boxW - 48, y + 44);
        });
      }
      return canvas;
    },
    // 卡片二：本周累计（主数据 + 7 日柱状图）
    drawWeekCard() {
      const t = this.t;
      const { canvas, ctx, W, PAD, colors } = this.drawChrome(
        t.share_card_week_title || "本周累计"
      );
      const ws = this.weekStats;
      this.drawHero(
        ctx,
        W,
        colors,
        this.fmtMins(ws.totalMins),
        t.share_week_focus || "本周专注"
      );
      // 两项小数据：活跃天数 + 日均
      const avg = ws.activeDays ? Math.round(ws.totalMins / ws.activeDays) : 0;
      const cols = [W * 0.3, W * 0.7];
      const small = [
        {
          num: ws.activeDays + (t.share_unit_days || "天"),
          label: t.share_active_days || "活跃天数",
        },
        { num: this.fmtMins(avg), label: t.share_daily_avg || "日均" },
      ];
      small.forEach((st, i) => {
        ctx.textAlign = "center";
        ctx.fillStyle = colors.amberLt;
        ctx.font = "700 52px system-ui, -apple-system, sans-serif";
        ctx.fillText(st.num, cols[i], 720);
        ctx.fillStyle = colors.muted;
        ctx.font = "500 28px system-ui, -apple-system, sans-serif";
        ctx.fillText(st.label, cols[i], 765);
      });
      // 柱状图容器
      const boxX = PAD;
      const boxY = 820;
      const boxW = W - PAD * 2;
      const boxH = 480;
      ctx.fillStyle = "rgba(255,255,255,0.05)";
      this.roundRect(ctx, boxX, boxY, boxW, boxH, 36);
      ctx.fill();
      const labels = t.share_weekdays || ["一", "二", "三", "四", "五", "六", "日"];
      const maxMins = Math.max(...ws.days.map((d) => d.minutes), 1);
      const innerPad = 60;
      const slot = (boxW - innerPad * 2) / 7;
      const barW = slot * 0.5;
      const baseY = boxY + boxH - 90;
      const maxBarH = boxH - 200;
      const todayIdx = (new Date().getDay() + 6) % 7;
      ws.days.forEach((d, i) => {
        const cx = boxX + innerPad + slot * i + slot / 2;
        const h = Math.round((d.minutes / maxMins) * maxBarH);
        const isToday = i === todayIdx;
        // 柱
        ctx.fillStyle = d.minutes
          ? isToday
            ? colors.amberLt
            : colors.amber
          : "rgba(255,255,255,0.1)";
        this.roundRect(ctx, cx - barW / 2, baseY - h, barW, Math.max(h, 6), 8);
        ctx.fill();
        // 顶部分钟数
        if (d.minutes) {
          ctx.textAlign = "center";
          ctx.fillStyle = "rgba(255,255,255,0.7)";
          ctx.font = "600 22px system-ui, -apple-system, sans-serif";
          ctx.fillText(String(d.minutes), cx, baseY - h - 16);
        }
        // 星期标签
        ctx.textAlign = "center";
        ctx.fillStyle = isToday ? colors.amberLt : colors.muted;
        ctx.font = "600 28px system-ui, -apple-system, sans-serif";
        ctx.fillText(labels[i], cx, baseY + 50);
      });
      return canvas;
    },
    // 卡片三：累计总览（三项核心数据 + 月度热力图）
    drawTotalCard() {
      const t = this.t;
      const s = this.summary || { totalHours: 0, totalTasks: 0, streak: 0 };
      const { canvas, ctx, W, PAD, colors } = this.drawChrome(
        t.share_card_total_title || "累计总览"
      );
      const stats = [
        {
          num: String(s.totalHours),
          unit: t.share_unit_hours || "小时",
          label: t.share_total_focus || "累计专注",
        },
        {
          num: String(s.streak),
          unit: t.share_unit_days || "天",
          label: t.share_streak || "连续打卡",
        },
        {
          num: String(s.totalTasks),
          unit: t.share_unit_times || "次",
          label: t.share_sessions || "专注次数",
        },
      ];
      const cols = [W * 0.22, W * 0.5, W * 0.78];
      stats.forEach((st, i) => {
        const cx = cols[i];
        ctx.textAlign = "center";
        ctx.fillStyle = colors.amberLt;
        ctx.font = "800 92px system-ui, -apple-system, sans-serif";
        ctx.fillText(st.num, cx, 510);
        ctx.fillStyle = colors.muted;
        ctx.font = "500 28px system-ui, -apple-system, sans-serif";
        ctx.fillText(st.unit, cx, 552);
        ctx.fillStyle = "rgba(255,255,255,0.8)";
        ctx.font = "600 30px system-ui, -apple-system, sans-serif";
        ctx.fillText(st.label, cx, 600);
      });
      // 热力图容器
      const boxX = PAD;
      const boxY = 690;
      const boxW = W - PAD * 2;
      const boxH = 600;
      ctx.fillStyle = "rgba(255,255,255,0.05)";
      this.roundRect(ctx, boxX, boxY, boxW, boxH, 36);
      ctx.fill();
      ctx.textAlign = "left";
      ctx.fillStyle = "rgba(255,255,255,0.85)";
      ctx.font = "700 32px system-ui, -apple-system, sans-serif";
      ctx.fillText(t.week_trend || "心流热力图", boxX + 48, boxY + 70);
      ctx.textAlign = "right";
      ctx.fillStyle = colors.amberLt;
      ctx.font = "700 30px system-ui, -apple-system, sans-serif";
      ctx.fillText(
        (t.share_today_flow || "今日心流") + " " + this.fmtMins(this.todayMinutes),
        boxX + boxW - 48,
        boxY + 70
      );
      const days = this.monthHeatmap || [];
      const gridCols = 7;
      const gridLeft = boxX + 48;
      const gridRight = boxX + boxW - 48;
      const gridTop = boxY + 120;
      // 可用高度：容器底部留 48 内边距。行数按当月天数计算（28~31 天 → 4~5 行）。
      const gridBottom = boxY + boxH - 48;
      const rows = Math.max(1, Math.ceil(days.length / gridCols));
      const gap = 14;
      // cell 尺寸同时受宽度和高度约束，取较小值，保证 5 行也不溢出容器。
      const cellByW = Math.floor(
        (gridRight - gridLeft - gap * (gridCols - 1)) / gridCols
      );
      const cellByH = Math.floor(
        (gridBottom - gridTop - gap * (rows - 1)) / rows
      );
      const cell = Math.max(1, Math.min(cellByW, cellByH));
      days.forEach((day, i) => {
        const r = Math.floor(i / gridCols);
        const c = i % gridCols;
        const x = gridLeft + c * (cell + gap);
        const y = gridTop + r * (cell + gap);
        let fill = "rgba(255,255,255,0.06)";
        if (day.minutes > 0) {
          const op = Math.min(0.25 + (day.minutes / 120) * 0.75, 1);
          fill = `rgba(245,158,11,${op})`;
        }
        ctx.fillStyle = fill;
        this.roundRect(ctx, x, y, cell, cell, 10);
        ctx.fill();
      });
      return canvas;
    },
    // 优先系统分享（移动端可分享到微信等），否则下载图片；作用于当前选中卡片
    async saveOrShare() {
      const card = this.shareCards[this.activeCard];
      if (!card) return;
      const blob =
        card.blob ||
        (await new Promise((res) => card.canvas.toBlob(res, "image/png")));
      const fileName = "zeno-" + card.key + "-card.png";
      if (this.canNativeShare) {
        try {
          const file = new File([blob], fileName, { type: "image/png" });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: this.t.share_card_title || "我的专注成绩单",
            });
            return;
          }
        } catch (e) {
          if (e && e.name === "AbortError") return; // 用户取消
        }
      }
      // 回退：浏览器下载
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      this.flashToast(this.t.share_saved_toast || "图片已保存");
    },
    flashToast(msg) {
      this.toast = msg;
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => (this.toast = ""), 2200);
    },
  },
  beforeDestroy() {
    clearTimeout(this._toastTimer);
  },
};
</script>
